//@flow
import queryString from 'qs';

const PROD_HOSTS = [
  'naturebox.com',
  'natureboxwellness.com',
];

class UrlData {

  //this is defined by flow, see http://www.saltycrane.com/blog/2016/06/flow-type-cheat-sheet/
  _getCurrentUrl():Location {
    return global.window && global.window.location;
  }

  _getPathname():string {
    const url:Location = this._getCurrentUrl();
    return url && url.pathname;
  }

  _getPathWithParams():string {
    const path = this._getPathname();
    const params = this.getSearch();
    return path + params;
  }

  _removeURLParameter(url:string, parameter:string):string {
    const urlparts = url.split('?');
    if (urlparts.length >= 2) {
      const prefix = encodeURIComponent(parameter)+'=';
      const params = urlparts[1].split(/[&;]/g);

      for (let i = params.length; i-- > 0;) {
        if (params[i].lastIndexOf(prefix, 0) !== -1) {
          params.splice(i, 1);
        }
      }
      url = urlparts[0] + (params.length > 0 ? '?' + params.join('&') : "");
      return url;
    } else {
      return url;
    }
  }

  setCurrentUrl(location:string) {
    window && (window.location = location);
  }

  getPathname():string {
    return this._getPathname() || '';
  }

  getHostname():string {
    const url = this._getCurrentUrl();
    return url && url.hostname;
  }

  getHost():string {
    const url = this._getCurrentUrl();
    return url && url.host;
  }

  getHref():string {
    const url = this._getCurrentUrl();
    return url && url.href;
  }

  getProtocol():string {
    const url = this._getCurrentUrl();
    return url && url.protocol;
  }

  getFullHostname():string {
    const protocol = this.getProtocol();
    const hostname = this.getHostname();
    const full = `${protocol}//${hostname}`;
    return full;
  }

  getFullHost():string {
    const protocol = this.getProtocol();
    const host = this.getHost();
    return `${protocol}//${host}`;
  }

  getHash():string {
    const url = this._getCurrentUrl();
    return (url && url.hash) || '';
  }

  getParsedQuery(search:string):Object {
    return queryString.parse(search, { plainObjects: true, ignoreQueryPrefix: true });
  }

  getSearch():string {
    return this._getCurrentUrl().search;
  }

  getOrigin():string {
    return this._getCurrentUrl().origin;
  }

  getRerouteUrl():string {
    const params = this.getParsedQuery(this.getSearch());
    const location = params.location;
    if (!location) return '/products';

    delete params.location;
    const stringified = '?' + queryString.stringify(params);
    return `/${location}` + (stringified === '?' ? '' : stringified);
  }

  parseUrlForKey(key:string):?string {
    const val = this.getParsedQuery(this.getSearch())[key];
    return val === '' ? null : val;
  }

  createRerouteUrl(dest:string):string {
    const loc = this._getCurrentUrl();

    if (!loc)
      return '';

    const path = loc.pathname.slice(1);
    const query = loc.search.slice(1);

    return `${dest}?location=${path}&${query}`;
  }

  getReorderToken():?string {
    return this.parseUrlForKey('reorder');
  }

  removeReorderToken() {
    let url = this._getPathWithParams();
    url = this._removeURLParameter(url, 'reorder');
    window && window.history && window.history.replaceState({}, "", url);
  }

  getQuoteItems():?string {
    return this.parseUrlForKey('sku');
  }

  removeParamAndUpdateHistory(param:string) {
    let url = this._getPathWithParams();
    url = this._removeURLParameter(url, param);
    window && window.history && window.history.replaceState({}, "", url);
  }

  removeQuoteItems() {
    let url = this._getPathWithParams();
    url = this._removeURLParameter(url, 'sku');
    window && window.history && window.history.replaceState({}, "", url);
  }

  removeMktgDataPreviewParam() {
    let url = this._getPathWithParams();
    url = this._removeURLParameter(url, 'nb_mktg_preview');
    window && window.history && window.history.replaceState({}, "", url);
  }

  getMktgDataPreview():?string {
    return this.parseUrlForKey('nb_mktg_preview');
  }

  getPromoCode():?string {
    return this.parseUrlForKey('promo_code');
  }

  getHideAmazon():?string {
    return this.parseUrlForKey('hideAmazon');
  }

  getToken():?string {
    return this.parseUrlForKey('token');
  }

  getOpenCart():?boolean {
    return Boolean(this.parseUrlForKey('open_cart'));
  }

  getCustomerGroup():?string {
    return this.parseUrlForKey('customer_group');
  }

  getShouldSubscribe():boolean {
    return Boolean(this.parseUrlForKey('should_subscribe'));
  }

  getShouldHideMsrpToggle():boolean {
    return Boolean(this.parseUrlForKey('should_hide_msrp_toggle'));
  }

  getShouldHideFriendbuy():boolean {
    return Boolean(this.parseUrlForKey('should_hide_friendbuy'));
  }

  getShouldHideCheckoutUpsellModal():boolean {
    return Boolean(this.parseUrlForKey('should_hide_checkout_upsell_modal'));
  }

  getShouldHideCheckoutMembershipOption():boolean {
    return Boolean(this.parseUrlForKey('should_hide_checkout_membership_option'));
  }

  getShouldHideCheckoutSubscribeOption():boolean {
    return Boolean(this.parseUrlForKey('should_hide_checkout_subscribe_option'));
  }

  getShouldHidePromoCodeInput():boolean {
    return Boolean(this.parseUrlForKey('should_hide_promo_code_input'));
  }

  makeUrlWithQuery(url:string, newQuery?:string):string {
    const oldQuery = this.getSearch();

    let query = "";
    if (newQuery) {
      query = oldQuery === "" ? `?${newQuery}` : `${oldQuery}&${newQuery}`;
    } else {
      query = oldQuery;
    }

    return `${url}${query}`;
  }

  getExperiments():Object {
    let experiments = this.parseUrlForKey('exp');
    if (!experiments) return {};

    experiments = [].concat(experiments);
    const expObj = {};
    experiments.forEach((exp) => {
      const parts = exp.split(':');
      expObj[parts[0]] = parts[1] || true;
    });

    return expObj;
  }

  showHelloBar():boolean {
    if (this.inHomepage() || this.inCatalog()) return true;
    switch (this._getPathname()) {
      case '/gifts':
      case '/collections':
      case '/welcome':
        return true;
    }
    return false;
  }

  inCart():boolean {
    const pathname = this._getPathname();
    return pathname.slice(0,5) === '/cart';
  }

  inHomepage():boolean  {
    const pathname = this._getPathname();
    return pathname === '/' || pathname === '/products' || pathname === '/homepage';
  }

  inCatalog():boolean  {
    return !this.inHomepage() && this._getPathname().startsWith('/product');
  }

  onCorporatePage():boolean {
    const pathname = this._getPathname();
    return pathname.slice(0,7) === '/office';
  }

  shouldOpenPasswordModal():boolean  {
    const pathname = this._getPathname();

    return pathname.includes('/account') &&
      !!this.parseUrlForKey('acc_set_pass_open');
  }

  getMagicLinkToken():?string {
    return this.parseUrlForKey('magiclink');
  }

  getSoftLinkToken():?string {
    return this.parseUrlForKey('softlink');
  }

  getMembershipPlan():?string {
    return this.parseUrlForKey('nb_membership_plan');
  }

  getOfficeUrl():string {
    const host = this.getFullHost();
    return `${host}/office`;
  }

  getNatureboxWellnessHostnames():string[] {
    return [
      'natureboxwellness.com',
      'staging.natureboxwellness.com',
    ];
  }

  getShouldHideFacebookAuth(hostname?:string):boolean {
    const currentHostname = hostname || this.getHostname();
    const nbWellnessHostnames = this.getNatureboxWellnessHostnames();
    return nbWellnessHostnames.includes(currentHostname);
  }

  getEngineApiUrl(){
    const hostname = window ? window.location.hostname : '';
    if (hostname.startsWith('staging.nbxlabs.co')) {
      return 'https://nb-engine-staging.nbxlabs.co';
    }
    else if (hostname.startsWith('cow.nbxlabs.co')) {
      return 'https://nb-engine-cow.nbxlabs.co';
    }
    else if (PROD_HOSTS.includes(hostname)) {
      return 'https://nb-engine.naturebox.com';
    }
    else {
      return 'http://localhost:4000';
    }
  }

  objectToQuery(params:{[string]:string|number}) {
    const items:string[] = [];
    Object.keys(params).forEach(key => {
      const value = params[key];
      items.push(`${key}=${value}`);
    });
    return items.join('&');
  }
}

export default new UrlData();
